import React, { useState } from "react";
import { Box, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArticleIcon from "@mui/icons-material/Article";
import { colors } from "../../../styles/theme";
import { LayerVisibility } from "../types";
import LayerControl from "./LayerControl";
import { ActionsMenuList } from "../../common/overlays/ActionsMenu";

interface MapAnimationsProps {
  isHovered: boolean;
  children: React.ReactNode;
  layerVisibility: LayerVisibility;
  onToggleLayer: (layerName: keyof LayerVisibility) => void;
  showLayerControl?: boolean;
  showMenuButton?: boolean;
  onShapefileUploadClick?: () => void;
  onAlkisUploadClick?: () => void;
}

/**
 * Animation-Wrapper für Map-Komponenten
 * Verwaltet Animationen und Menu-Button
 */
const MapAnimations: React.FC<MapAnimationsProps> = ({
  isHovered,
  children,
  layerVisibility,
  onToggleLayer,
  showLayerControl = true,
  showMenuButton = true,
  onShapefileUploadClick,
  onAlkisUploadClick,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <motion.div
      layoutId="map-container"
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "transparent",
        borderRadius: "12px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease-in-out",
        margin: 0,
        padding: 0,
      }}
    >
      {children}

      {/* Menu-Button - nur anzeigen wenn Geometrie vorhanden */}
      <AnimatePresence>
        {showMenuButton && (onShapefileUploadClick || onAlkisUploadClick) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              zIndex: theme.zIndex.modal - 200,
            }}
          >
            <Box
              sx={{
                backgroundColor: colors.glass.backgroundSolid,
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.background.paper,
                  transform: "scale(1.1)",
                },
              }}
              onClick={handleMenuClick}
            >
              <MoreVertIcon
                sx={{
                  fontSize: 20,
                  color: colors.primary.main,
                }}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dropdown-Menu für Upload-Optionen; der runde Knopf oben bleibt kartentypisch */}
      <ActionsMenuList
        anchor={menuAnchor}
        onClose={handleMenuClose}
        items={[
          ...(onShapefileUploadClick
            ? [
                {
                  label: "Fläche hochladen (Shape)",
                  icon: <UploadFileIcon fontSize="small" />,
                  onClick: onShapefileUploadClick,
                },
              ]
            : []),
          ...(onAlkisUploadClick
            ? [
                {
                  label: "Flurstücke hochladen (ALKIS)",
                  icon: <ArticleIcon fontSize="small" />,
                  onClick: onAlkisUploadClick,
                },
              ]
            : []),
        ]}
      />

      {/* Layer Controls - nur mit Hover-Animation wenn Geometrie vorhanden */}
      {showLayerControl && showMenuButton && (
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ y: 100, x: "-50%" }}
              animate={{ y: 0, x: "-50%" }}
              exit={{ y: 100, x: "-50%" }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
              }}
              style={{
                position: "absolute",
                left: "50%",
                bottom: 10,
                zIndex: 1000,
              }}
            >
              <LayerControl
                layerVisibility={layerVisibility}
                onToggleLayer={onToggleLayer}
                isFullscreen={true}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  );
};

export default MapAnimations;
